import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import {
  CheckCircle,
  XCircle,
  RefreshCw,
  Package,
  Sparkles,
  IndianRupee,
  History,
  CreditCard,
} from "lucide-react";
import { BookingType } from "../../../Data/package.dto";
import { useAppDispatch, useAppSelector } from "../../../Redux/Hooks";
import { getPackageBookingAPI } from "../../../Redux/ApiCalls/Dashboard/PackageAPI";
import GenerateBookPackagePaymentModal from "../../atoms/Dashboard/PackageBook/GenerateLinkModal";
import ShowBookingTransactionModal from "../../atoms/Dashboard/PackageBook/ShowBookingTransactionModal";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "./Marketplace/MarketplaceEmptyState";
import { device } from "../../atoms/Utlis/media";
import {
  MenuContainer,
  ThreeDotsButton,
  Dot,
  DropdownMenu,
  MenuItem,
  ItemLabel,
  Backdrop,
} from "./documentSharedStyles";
import {
  ListHeader,
  ListTitle,
  CountBadge,
  TableWrap,
  PaymentsTable,
  AmountCell,
  RemainingCell,
  StatusPill,
} from "../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";

export type Booking = {
  id: number;
  customer_name: string | null;
  package_title: string;
  booked_price: string;
  received_amount: string;
  remaining_amount: string;
  status: string;
  created_at: string;
};

function formatCurrency(value: string) {
  const n = Number(value);
  if (Number.isNaN(n)) return "-";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AllCustomerTransactionPage() {
  const [selectedBooking, setSelectedBooking] = useState<BookingType>();
  const [paymentLinkModal, setPaymentLinkModal] = useState(false);
  const [transactionModal, setTransactionModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const dispatch = useAppDispatch();
  const { PackageBookings } = useAppSelector((state) => state.package);

  useEffect(() => {
    fetchBooking();
  }, []);

  useEffect(() => {
    if (PackageBookings.isUpdated && !PackageBookings.loading) {
      fetchBooking();
    }
  }, [PackageBookings.isUpdated]);

  const fetchBooking = async () => {
    dispatch(getPackageBookingAPI());
  };

  const toggleMenu = (bookingId: number) => {
    setOpenMenuId(openMenuId === bookingId ? null : bookingId);
  };

  const handleMakePayment = (booking: Booking) => {
    setSelectedBooking(booking as BookingType);
    setPaymentLinkModal(true);
    setOpenMenuId(null);
  };

  const handleTransactionHistory = (booking: Booking) => {
    setSelectedBooking(booking as BookingType);
    setTransactionModal(true);
    setOpenMenuId(null);
  };

  const isLoading = PackageBookings.loading;
  const bookings = PackageBookings.data ?? [];
  const isEmpty = !isLoading && bookings.length === 0;

  const totalReceived = bookings.reduce((sum, b) => sum + Number(b.received_amount || 0), 0);
  const totalRemaining = bookings.reduce((sum, b) => sum + Number(b.remaining_amount || 0), 0);

  return (
    <PageContainer>
      {transactionModal && selectedBooking && (
        <ShowBookingTransactionModal
          onClose={() => setTransactionModal(false)}
          isOpen={transactionModal}
          bookingId={selectedBooking.id.toString()}
        />
      )}
      {paymentLinkModal && selectedBooking && (
        <GenerateBookPackagePaymentModal
          onClose={() => setPaymentLinkModal(false)}
          refreshData={() => fetchBooking()}
          isOpen={paymentLinkModal}
          booking_data={selectedBooking}
        />
      )}

      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Customer payments
              </HeroBadge>
              <PageTitle className="linear-text">Package Bookings</PageTitle>
              <PageSubtitle>
                Track package bookings, payment progress, and transaction history for your customers.
              </PageSubtitle>
            </div>
          </HeroText>
          <RefreshBtn type="button" onClick={fetchBooking} title="Refresh">
            <RefreshCw size={18} strokeWidth={2.5} />
          </RefreshBtn>
        </HeroContent>
      </Hero>

      {!isLoading && (
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <Package size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>
                {PackageBookings.booked_packages?.total_packages_booked ?? 0}
              </WalletHeroValue>
              <WalletHeroLabel>Total packages booked</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>

          <StatCard>
            <StatIcon $tone="#31a66a">
              <CheckCircle size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>
                {PackageBookings.booked_packages?.completed_package_requests ?? 0}
              </StatValue>
              <StatLabel>Completed</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard>
            <StatIcon $tone="#e44862">
              <XCircle size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>
                {PackageBookings.booked_packages?.cancelled_package_requests ?? 0}
              </StatValue>
              <StatLabel>Cancelled</StatLabel>
            </StatBody>
          </StatCard>

          <StatCard>
            <StatIcon $tone="#35b7c8">
              <IndianRupee size={16} />
            </StatIcon>
            <StatBody>
              <StatValue>₹{formatCurrency(String(totalReceived))}</StatValue>
              <StatLabel>Total received</StatLabel>
            </StatBody>
          </StatCard>
        </StatsGrid>
      )}

      <Panel>
        {isLoading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : isEmpty ? (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={<Package size={36} strokeWidth={1.5} />}
            title="No package bookings yet"
            description="When customers book your packages, their payments and booking details will appear here."
            actions={[
              {
                label: "Refresh",
                onClick: fetchBooking,
                icon: <RefreshCw size={15} />,
                variant: "secondary",
              },
            ]}
            hint={{
              icon: <Sparkles size={13} />,
              text: "Request payments & view transaction history from each booking",
            }}
          />
        ) : (
          <>
            <DesktopTableWrap>
              <ListHeader>
                <ListTitle>All bookings</ListTitle>
                <CountBadge>
                  {bookings.length} record{bookings.length !== 1 ? "s" : ""}
                </CountBadge>
              </ListHeader>

              <TableWrap>
                <PaymentsTable $minWidth={980}>
                  <thead>
                    <tr>
                      {[
                        "Customer",
                        "Package",
                        "Booked",
                        "Received",
                        "Remaining",
                        "Status",
                        "Created",
                        "Actions",
                      ].map((h) => (
                        <th key={h}><span>{h}</span></th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id}>
                        <td>
                          <CustomerCell>
                            <CustomerAvatar aria-hidden="true">
                              {(b.customer_name?.trim()?.[0] ?? "?").toUpperCase()}
                            </CustomerAvatar>
                            <CustomerName>{b.customer_name ?? "Unknown customer"}</CustomerName>
                          </CustomerCell>
                        </td>
                        <td>
                          <PackageName>{b.package_title}</PackageName>
                        </td>
                        <td><AmountCell>₹{formatCurrency(b.booked_price)}</AmountCell></td>
                        <td><AmountCell>₹{formatCurrency(b.received_amount)}</AmountCell></td>
                        <td>
                          {Number(b.remaining_amount) > 0 ? (
                            <RemainingCell>₹{formatCurrency(b.remaining_amount)}</RemainingCell>
                          ) : (
                            <AmountCell>₹{formatCurrency(b.remaining_amount)}</AmountCell>
                          )}
                        </td>
                        <td>
                          <StatusPill $status={b.status}>
                            {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                          </StatusPill>
                        </td>
                        <td>{formatDate(b.created_at)}</td>
                        <td>
                          <MenuContainer>
                            <ThreeDotsButton
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu(b.id);
                              }}
                            >
                              <Dot />
                              <Dot />
                              <Dot />
                            </ThreeDotsButton>
                            {openMenuId === b.id && (
                              <>
                                <Backdrop onClick={() => setOpenMenuId(null)} />
                                <DropdownMenu onClick={(e) => e.stopPropagation()}>
                                  {b.status?.toLowerCase() !== "completed" && Number(b.remaining_amount) > 0 && (
                                    <MenuItem
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMakePayment(b);
                                      }}
                                    >
                                      <MenuItemLabel>
                                        <CreditCard size={14} />
                                        Request Payment
                                      </MenuItemLabel>
                                    </MenuItem>
                                  )}
                                  <MenuItem
                                    type="button"
                                    onClick={() => handleTransactionHistory(b)}
                                  >
                                    <MenuItemLabel>
                                      <History size={14} />
                                      Transaction History
                                    </MenuItemLabel>
                                  </MenuItem>
                                </DropdownMenu>
                              </>
                            )}
                          </MenuContainer>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </DesktopTableWrap>

            <MobileList>
              {bookings.map((b) => (
                <MobileCard key={b.id}>
                  <MobileCardTop>
                    <MobileCustomer>{b.customer_name ?? "-"}</MobileCustomer>
                    <StatusPill $status={b.status}>
                      {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                    </StatusPill>
                  </MobileCardTop>
                  <MobileRow>
                    <MobileLabel>Package</MobileLabel>
                    <MobileValue>{b.package_title}</MobileValue>
                  </MobileRow>
                  <MobileRow>
                    <MobileLabel>Booked</MobileLabel>
                    <MobileValue>₹{formatCurrency(b.booked_price)}</MobileValue>
                  </MobileRow>
                  <MobileRow>
                    <MobileLabel>Received</MobileLabel>
                    <MobileValue>₹{formatCurrency(b.received_amount)}</MobileValue>
                  </MobileRow>
                  <MobileRow>
                    <MobileLabel>Remaining</MobileLabel>
                    <MobileValue $highlight={Number(b.remaining_amount) > 0}>
                      ₹{formatCurrency(b.remaining_amount)}
                    </MobileValue>
                  </MobileRow>
                  <MobileDate>{formatDate(b.created_at)}</MobileDate>
                  <MobileActions>
                    {b.status?.toLowerCase() !== "completed" && Number(b.remaining_amount) > 0 && (
                      <MobileActionBtn type="button" onClick={() => handleMakePayment(b)}>
                        Request Payment
                      </MobileActionBtn>
                    )}
                    <MobileActionBtn type="button" $secondary onClick={() => handleTransactionHistory(b)}>
                      Transaction History
                    </MobileActionBtn>
                  </MobileActions>
                </MobileCard>
              ))}
            </MobileList>

            <FooterRow>
              {/* <FooterText>Showing {bookings.length} records</FooterText> */}
              {totalRemaining > 0 && (
                <FooterHighlight>
                  ₹{formatCurrency(String(totalRemaining))} pending
                </FooterHighlight>
              )}
            </FooterRow>
          </>
        )}
      </Panel>
    </PageContainer>
  );
}

const gradient = "linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)";

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`;

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`;

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`;

const RefreshBtn = styled.button`
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: rgba(201, 77, 170, 0.25);
    color: #c94daa;
    transform: translateY(-1px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  flex-shrink: 0;
  @media ${device.laptop} { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`;

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`;

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`;

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(23, 23, 23, 0.04);
`;

const StatIcon = styled.div<{ $tone: string }>`
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const StatBody = styled.div`
  min-width: 0;
`;

const StatValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #171717;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  margin-top: 2px;
`;

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`;

const DesktopTableWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  padding: 24px 24px 0;
  @media (max-width: 768px) { display: none; }
`;

const CustomerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CustomerAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 800;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
  border: 1px solid rgba(201, 77, 170, 0.14);
`;

const CustomerName = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PackageName = styled.span`
  display: block;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MenuItemLabel = styled(ItemLabel)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const MobileList = styled.div`
  display: none;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  overflow-y: auto;
  @media (max-width: 768px) { display: flex; }
`;

const MobileCard = styled.div`
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 16px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
`;

const MobileCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const MobileCustomer = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const MobileRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
`;

const MobileLabel = styled.span`
  color: rgba(23, 23, 23, 0.5);
  font-weight: 600;
`;

const MobileValue = styled.span<{ $highlight?: boolean }>`
  font-weight: 700;
  color: ${(p) => (p.$highlight ? "#e44862" : "#171717")};
`;

const MobileDate = styled.p`
  margin: 8px 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.45);
`;

const MobileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileActionBtn = styled.button<{ $secondary?: boolean }>`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: ${(p) => (p.$secondary ? "1px solid rgba(23, 23, 23, 0.1)" : "none")};
  background: ${(p) => (p.$secondary ? "#fff" : gradient)};
  color: ${(p) => (p.$secondary ? "rgba(23, 23, 23, 0.72)" : "#fff")};
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 22px 20px;
  // border-top: 1px solid rgba(23, 23, 23, 0.06);
  flex-shrink: 0;
`;

const FooterText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const FooterHighlight = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #e44862;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(228, 72, 98, 0.1);
`;
